<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import StudentHeader from "@/components/StudentHeader.vue";
import TutorHeader from "@/components/TutorHeader.vue";
import { DEFAULT_STUDENT_CRITERIA, type FeedbackCriterion } from "@/types/feedback";
import { useFeedbackForm, getRatingLabel } from "@/composables/useFeedbackForm";
import FeedbackCard from "@/components/Student-Tutor/FeedbackCard.vue";
import FileReport from "@/components/FileReport.vue";
import { auth, onAuthStateChanged, db, doc, getDoc } from "@/firebase";

const route = useRoute();
const { isSubmitting, error, success, submitFeedback, navigateBack, navigateToSessions } =
  useFeedbackForm();

// User state management
const currentUser = ref<any>(null);
const userType = ref<string | null>(null);
const isLoading = ref(true);

// Reactive data
const criteria = ref<FeedbackCriterion[]>(
  DEFAULT_STUDENT_CRITERIA.map((criterion) => ({
    ...criterion,
    rating: null as number | null, // No default rating
  }))
);

const additionalComments = ref("");

// Mock session data - in real app, this would come from API or props
const sessionInfo = ref({
  studentName: "Student Name",
  tutorName: "Maria Isabella Santos",
  subject: "Mathematics",
  date: new Date().toLocaleDateString(),
  sessionId: (route.params.sessionId as string) || "session-123",
  studentId: "student-user-id", // Would come from session data
  tutorId: "current-user-id", // Would come from auth
});

// Computed properties
const averageRating = computed(() => {
  const validRatings = criteria.value.filter((criterion) => criterion.rating !== null);
  if (validRatings.length === 0) return "0.0";
  const total = validRatings.reduce((sum, criterion) => sum + (criterion.rating as number), 0);
  return (total / validRatings.length).toFixed(1);
});

const completedCount = computed(() => {
  // Count how many criteria have been explicitly rated (not null)
  return criteria.value.filter((criterion) => criterion.rating !== null).length;
});

// Methods
const updateRating = (criterionId: string, rating: number) => {
  const criterion = criteria.value.find((c) => c.id === criterionId);
  if (criterion) {
    criterion.rating = rating;
  }
};

const handleSubmitFeedback = async () => {
  const success = await submitFeedback(
    sessionInfo.value.sessionId,
    sessionInfo.value.tutorId,
    sessionInfo.value.studentId,
    criteria.value,
    additionalComments.value,
    sessionInfo.value.subject
  );

  if (success) {
    // Show success message and redirect after a short delay
    setTimeout(() => {
      navigateToSessions();
    }, 2000);
  }
};

// Handle back button click
const goBack = () => {
  // Go back to previous page
  window.history.back();
};

// Get user type from Firebase
const getUserType = async (uid: string) => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.userType || null;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user type:", error);
    return null;
  }
};

onMounted(() => {
  document.title = "ElevatED – Feedback";

  // Listen for auth state changes and get user type
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      currentUser.value = user;
      userType.value = await getUserType(user.uid);
    } else {
      currentUser.value = null;
      userType.value = null;
    }
    isLoading.value = false;
  });

  // TODO: Fetch session data based on sessionId from route params
  // TODO: Get current user info from auth
});
</script>

<template>
  <div class="min-h-screen" :class="userType === 'student' ? 'student-page-wrapper' : 'bg-gray-50'">
    <!-- Conditional Header Rendering -->
    <StudentHeader v-if="userType === 'student'" />
    <TutorHeader v-else-if="userType === 'tutor'" />
    <StudentHeader v-else />
    <FileReport />

    <!-- Main Content -->
    <div class="pt-42 pb-16 px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mx-auto">
        <!-- Header Section -->
        <div class="text-center mb-6">
          <div class="flex items-center justify-center mb-3 relative">
            <!-- Back Button -->
            <div class="back-button" @click="goBack">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="size-6"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <h1 class="text-4xl font-black text-gray-900">Feedback & Rating</h1>
          </div>
          <p class="text-gray-600 text-sm font-medium">
            Help improve our tutoring community with your honest feedback.
          </p>
        </div>
        <!-- Feedback Form -->
        <FeedbackCard />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Back button styling from StudentRegisterView.vue */
.back-button {
  width: 60px;
  height: 60px;
  background: white;
  border: 3px solid #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  color: #000000;
  transition: all 0.3s ease;
  flex-shrink: 0;
  position: absolute;
  left: 0;
}

.back-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  color: #000000;
}

.back-button svg {
  width: 30px;
  height: 30px;
}

/* Custom styles for the feedback form */
.border-3 {
  border-width: 3px;
}

/* Smooth transitions for interactive elements */
button {
  transition: all 0.2s ease-in-out;
}

/* Rating stars hover effect */
.rating-star {
  transition: color 0.2s ease-in-out;
}

/* Form field focus styles */
textarea:focus {
  outline: none;
}

/* Radio button styling */
.rating-radio {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.rating-radio:checked {
  border-color: currentColor;
  background-color: transparent;
}

.rating-radio:checked::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
}

.rating-radio:hover {
  border-color: currentColor;
  opacity: 0.8;
}

/* Color specific styles for radio buttons */
.rating-radio.text-green-600:checked {
  border-color: #16a34a;
}

.rating-radio.text-green-600:checked::after {
  background-color: #16a34a;
}

.rating-radio.text-blue-600:checked {
  border-color: #2563eb;
}

.rating-radio.text-blue-600:checked::after {
  background-color: #2563eb;
}

.rating-radio.text-gray-600:checked {
  border-color: #4b5563;
}

.rating-radio.text-gray-600:checked::after {
  background-color: #4b5563;
}

.rating-radio.text-orange-600:checked {
  border-color: #ea580c;
}

.rating-radio.text-orange-600:checked::after {
  background-color: #ea580c;
}

.rating-radio.text-red-600:checked {
  border-color: #dc2626;
}

.rating-radio.text-red-600:checked::after {
  background-color: #dc2626;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .back-button {
    width: 36px;
    height: 36px;
  }

  .back-button svg {
    width: 18px;
    height: 18px;
  }

  .pt-32 {
    padding-top: 8rem;
  }

  .max-w-3xl {
    max-width: 100%;
  }

  .p-6 {
    padding: 1.5rem;
  }

  .text-2xl {
    font-size: 1.5rem;
  }

  .grid-cols-12 {
    display: block;
  }

  .col-span-1,
  .col-span-7,
  .col-span-4 {
    display: block;
    margin-bottom: 0.75rem;
  }

  .col-span-4 {
    margin-bottom: 0;
  }

  .space-x-3 {
    margin-left: 0.5rem;
  }
}

@media (max-width: 480px) {
  .back-button {
    width: 32px;
    height: 32px;
  }

  .back-button svg {
    width: 16px;
    height: 16px;
  }

  .pt-32 {
    padding-top: 6rem;
  }

  .p-6 {
    padding: 1rem;
  }

  .p-4 {
    padding: 0.75rem;
  }

  .text-2xl {
    font-size: 1.25rem;
  }

  .space-x-3 > div:first-child {
    margin-right: 0.5rem;
  }
}

/* Student background styling - same as StudentSessionView.vue */
.student-page-wrapper {
  display: flex;
  flex-direction: column;
  background: url("/student-reg-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
}
</style>
